import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users,
  Package, Star, Calendar, Download, Filter, Eye,
  ChevronLeft, ChevronRight, Activity, Target, Award,
  ArrowUpRight, ArrowDownRight, Clock, Share2,
  BarChart3, LineChart as LineChartIcon, PieChart as PieChartIcon,
  Zap, Sparkles, Crown, Rocket, Leaf
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerAnalytics = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [chartType, setChartType] = useState('revenue');
  const [currentPage, setCurrentPage] = useState(1);

  // Revenue Data
  const revenueData = [
    { month: 'Jan', revenue: 325000, orders: 245, profit: 210000, customers: 320 },
    { month: 'Feb', revenue: 412000, orders: 278, profit: 278000, customers: 380 },
    { month: 'Mar', revenue: 398000, orders: 312, profit: 265000, customers: 450 },
    { month: 'Apr', revenue: 456000, orders: 345, profit: 310000, customers: 520 },
    { month: 'May', revenue: 523000, orders: 389, profit: 367000, customers: 610 },
    { month: 'Jun', revenue: 498000, orders: 367, profit: 348000, customers: 580 },
    { month: 'Jul', revenue: 567000, orders: 412, profit: 397000, customers: 650 },
    { month: 'Aug', revenue: 589000, orders: 425, profit: 412000, customers: 710 },
    { month: 'Sep', revenue: 578000, orders: 418, profit: 404000, customers: 690 },
    { month: 'Oct', revenue: 612000, orders: 435, profit: 428000, customers: 780 },
    { month: 'Nov', revenue: 645000, orders: 448, profit: 451000, customers: 850 },
    { month: 'Dec', revenue: 720000, orders: 465, profit: 504000, customers: 950 },
  ];

  // Category Performance
  const categoryData = [
    { name: 'Indoor Plants', revenue: 1250000, growth: 15, percentage: 35, color: '#10b981' },
    { name: 'Outdoor Plants', revenue: 890000, growth: 12, percentage: 25, color: '#3b82f6' },
    { name: 'Flowering Plants', revenue: 720000, growth: 18, percentage: 20, color: '#f59e0b' },
    { name: 'Air Purifying', revenue: 430000, growth: 22, percentage: 12, color: '#8b5cf6' },
    { name: 'Others', revenue: 285000, growth: 8, percentage: 8, color: '#ef4444' },
  ];

  // Daily Sales Data
  const dailySalesData = [
    { day: 'Mon', sales: 42000, orders: 45, avgOrderValue: 933 },
    { day: 'Tue', sales: 38000, orders: 42, avgOrderValue: 905 },
    { day: 'Wed', sales: 56000, orders: 58, avgOrderValue: 966 },
    { day: 'Thu', sales: 49000, orders: 52, avgOrderValue: 942 },
    { day: 'Fri', sales: 68000, orders: 67, avgOrderValue: 1015 },
    { day: 'Sat', sales: 89000, orders: 89, avgOrderValue: 1000 },
    { day: 'Sun', sales: 75000, orders: 78, avgOrderValue: 962 },
  ];

  // Top Products
  const topProducts = [
    { name: 'Monstera Deliciosa', revenue: 560250, sales: 1245, growth: 15, rating: 4.9 },
    { name: 'Snake Plant', revenue: 295000, sales: 987, growth: 8, rating: 4.8 },
    { name: 'Fiddle Leaf Fig', revenue: 744600, sales: 876, growth: 22, rating: 4.9 },
    { name: 'Peace Lily', revenue: 267750, sales: 765, growth: 5, rating: 4.7 },
    { name: 'Aloe Vera', revenue: 98100, sales: 654, growth: -2, rating: 4.6 },
  ];

  // Monthly Comparison
  const monthlyComparison = {
    revenue: { current: 612000, previous: 523000, change: 17 },
    orders: { current: 435, previous: 389, change: 11.8 },
    customers: { current: 780, previous: 610, change: 27.9 },
    avgOrderValue: { current: 1407, previous: 1344, change: 4.7 },
  };

  const stats = {
    totalRevenue: 612000,
    totalOrders: 435,
    totalCustomers: 780,
    avgOrderValue: 1407,
    conversionRate: 3.2,
    growthRate: 17,
  };

  const StatCard = ({ title, value, change, icon: Icon, color, bgColor, prefix = '', suffix = '' }) => (
    <div className="stat-card">
      <div className="stat-header">
        <span className="stat-title">{title}</span>
        <div className="stat-icon" style={{ backgroundColor: bgColor }}>
          <Icon size={18} color={color} />
        </div>
      </div>
      <div className="stat-value">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</div>
      <div className="stat-footer">
        <span className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
          {change >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {Math.abs(change)}%
        </span>
        <span className="stat-period">vs last month</span>
      </div>
    </div>
  );

  const getGrowthColor = (growth) => {
    if (growth > 0) return '#10b981';
    if (growth < 0) return '#ef4444';
    return '#64748b';
  };

  return (
    <div className="partner-analytics-modern">
      {/* Header */}
      <div className="analytics-header">
        <div>
          <h1 className="page-title">Advanced Analytics</h1>
          <p className="page-subtitle">Deep insights into your platform performance</p>
        </div>
        <div className="header-actions">
          <div className="time-range">
            <Calendar size={16} />
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <button className="btn-export">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard title="Total Revenue" value={stats.totalRevenue} change={17} icon={DollarSign} color="#10b981" bgColor="#d1fae5" prefix="₹" />
        <StatCard title="Total Orders" value={stats.totalOrders} change={11.8} icon={ShoppingBag} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Total Customers" value={stats.totalCustomers} change={27.9} icon={Users} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Avg Order Value" value={stats.avgOrderValue} change={4.7} icon={Activity} color="#f59e0b" bgColor="#fed7aa" prefix="₹" />
      </div>

      {/* Monthly Comparison Insights */}
      <div className="comparison-card">
        <div className="comparison-header">
          <h3>Monthly Performance Comparison</h3>
          <p>October vs September 2024</p>
        </div>
        <div className="comparison-grid">
          <div className="comparison-item">
            <span className="comparison-label">Revenue</span>
            <div className="comparison-values">
              <span className="current">₹{monthlyComparison.revenue.current.toLocaleString()}</span>
              <span className="vs">vs</span>
              <span className="previous">₹{monthlyComparison.revenue.previous.toLocaleString()}</span>
            </div>
            <div className="comparison-progress">
              <div className="progress-fill" style={{ width: `${(monthlyComparison.revenue.current / monthlyComparison.revenue.previous) * 100}%` }}></div>
            </div>
            <div className="comparison-change positive">
              <TrendingUp size={14} />
              +{monthlyComparison.revenue.change}%
            </div>
          </div>
          <div className="comparison-item">
            <span className="comparison-label">Orders</span>
            <div className="comparison-values">
              <span className="current">{monthlyComparison.orders.current}</span>
              <span className="vs">vs</span>
              <span className="previous">{monthlyComparison.orders.previous}</span>
            </div>
            <div className="comparison-progress">
              <div className="progress-fill" style={{ width: `${(monthlyComparison.orders.current / monthlyComparison.orders.previous) * 100}%` }}></div>
            </div>
            <div className="comparison-change positive">
              <TrendingUp size={14} />
              +{monthlyComparison.orders.change}%
            </div>
          </div>
          <div className="comparison-item">
            <span className="comparison-label">Customers</span>
            <div className="comparison-values">
              <span className="current">{monthlyComparison.customers.current}</span>
              <span className="vs">vs</span>
              <span className="previous">{monthlyComparison.customers.previous}</span>
            </div>
            <div className="comparison-progress">
              <div className="progress-fill" style={{ width: `${(monthlyComparison.customers.current / monthlyComparison.customers.previous) * 100}%` }}></div>
            </div>
            <div className="comparison-change positive">
              <TrendingUp size={14} />
              +{monthlyComparison.customers.change}%
            </div>
          </div>
          <div className="comparison-item">
            <span className="comparison-label">Avg Order Value</span>
            <div className="comparison-values">
              <span className="current">₹{monthlyComparison.avgOrderValue.current}</span>
              <span className="vs">vs</span>
              <span className="previous">₹{monthlyComparison.avgOrderValue.previous}</span>
            </div>
            <div className="comparison-progress">
              <div className="progress-fill" style={{ width: `${(monthlyComparison.avgOrderValue.current / monthlyComparison.avgOrderValue.previous) * 100}%` }}></div>
            </div>
            <div className="comparison-change positive">
              <TrendingUp size={14} />
              +{monthlyComparison.avgOrderValue.change}%
            </div>
          </div>
        </div>
      </div>

      {/* Main Chart */}
      <div className="chart-card large">
        <div className="chart-header">
          <div>
            <h3>Performance Overview</h3>
            <p>Monthly revenue, orders, and customer trends</p>
          </div>
          <div className="chart-controls">
            <button className={`control-btn ${chartType === 'revenue' ? 'active' : ''}`} onClick={() => setChartType('revenue')}>Revenue</button>
            <button className={`control-btn ${chartType === 'orders' ? 'active' : ''}`} onClick={() => setChartType('orders')}>Orders</button>
            <button className={`control-btn ${chartType === 'customers' ? 'active' : ''}`} onClick={() => setChartType('customers')}>Customers</button>
          </div>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={revenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
              />
              <Legend />
              {chartType === 'revenue' && (
                <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue (₹)" />
              )}
              {chartType === 'orders' && (
                <Bar yAxisId="right" dataKey="orders" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Orders" />
              )}
              {chartType === 'customers' && (
                <Line yAxisId="right" type="monotone" dataKey="customers" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 5, fill: '#8b5cf6' }} name="Customers" />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="two-column-grid">
        {/* Category Performance */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Category Performance</h3>
            <p>Revenue by product category</p>
          </div>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height="220px">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="revenue"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="category-list">
            {categoryData.map((cat, idx) => (
              <div key={idx} className="category-item">
                <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                <span className="category-name">{cat.name}</span>
                <span className="category-revenue">₹{cat.revenue.toLocaleString()}</span>
                <span className={`category-growth ${cat.growth >= 0 ? 'positive' : 'negative'}`}>
                  {cat.growth >= 0 ? '+' : ''}{cat.growth}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Top Performing Products</h3>
            <p>Best sellers by revenue</p>
          </div>
          <div className="top-products-list">
            {topProducts.map((product, idx) => (
              <div key={idx} className="product-item">
                <div className="product-rank">#{idx + 1}</div>
                <div className="product-info">
                  <div className="product-name">{product.name}</div>
                  <div className="product-stats">
                    <span>📦 {product.sales} sold</span>
                    <span>⭐ {product.rating}</span>
                  </div>
                </div>
                <div className="product-revenue">₹{product.revenue.toLocaleString()}</div>
                <div className={`product-growth ${product.growth >= 0 ? 'positive' : 'negative'}`}>
                  {product.growth >= 0 ? '+' : ''}{product.growth}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Sales Trend */}
      <div className="chart-card">
        <div className="chart-header">
          <h3>Daily Sales Trend</h3>
          <p>Last 7 days performance</p>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailySalesData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                formatter={(value) => [`₹${value.toLocaleString()}`, 'Sales']}
              />
              <Bar dataKey="sales" fill="#10b981" radius={[8, 8, 0, 0]} />
              <Line type="monotone" dataKey="sales" stroke="#059669" strokeWidth={3} dot={{ r: 4, fill: '#059669' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key Insights */}
      <div className="insights-card">
        <div className="insights-header">
          <Zap size={20} />
          <h3>Key Insights & Recommendations</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div>
              <h4>Revenue Growth Acceleration</h4>
              <p>Your revenue has grown 17% this month. Outdoor plants category is the top performer with 22% growth.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">👥</div>
            <div>
              <h4>Customer Acquisition</h4>
              <p>New customer acquisition increased by 28%. Consider launching a referral program to maintain momentum.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🪴</div>
            <div>
              <h4>Product Opportunity</h4>
              <p>Fiddle Leaf Fig shows 22% growth. Increase stock and marketing focus on this high-demand product.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🎯</div>
            <div>
              <h4>Conversion Optimization</h4>
              <p>AOV increased by 4.7% to ₹1,407. Continue upselling strategies to maintain this trend.</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .partner-analytics-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .analytics-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .time-range {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
        }

        .time-range select {
          border: none;
          background: transparent;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          transition: all 0.3s ease;
          border: 1px solid #e8ecef;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .stat-title {
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
        }

        .stat-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .stat-footer {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stat-change {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .stat-change.positive {
          color: #10b981;
        }

        .stat-change.negative {
          color: #ef4444;
        }

        .stat-period {
          font-size: 11px;
          color: #94a3b8;
        }

        /* Comparison Card */
        .comparison-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 28px;
          border: 1px solid #e8ecef;
        }

        .comparison-header {
          margin-bottom: 20px;
        }

        .comparison-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .comparison-header p {
          font-size: 12px;
          color: #94a3b8;
          margin: 4px 0 0;
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }

        .comparison-item {
          padding: 16px;
          background: #f8fafc;
          border-radius: 16px;
        }

        .comparison-label {
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
          display: block;
          margin-bottom: 8px;
        }

        .comparison-values {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .current {
          font-size: 18px;
          font-weight: 700;
          color: #10b981;
        }

        .vs {
          font-size: 11px;
          color: #94a3b8;
        }

        .previous {
          font-size: 14px;
          color: #64748b;
          text-decoration: line-through;
        }

        .comparison-progress {
          height: 6px;
          background: #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-fill {
          height: 100%;
          background: #10b981;
          border-radius: 10px;
        }

        .comparison-change {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .comparison-change.positive {
          color: #10b981;
        }

        /* Charts */
        .chart-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e8ecef;
        }

        .chart-card.large {
          margin-bottom: 24px;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .chart-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .chart-header p {
          font-size: 12px;
          color: #94a3b8;
          margin: 4px 0 0;
        }

        .chart-controls {
          display: flex;
          gap: 8px;
        }

        .control-btn {
          padding: 4px 16px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 40px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .control-btn.active {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        .chart-container {
          height: 320px;
        }

        .pie-chart-container {
          display: flex;
          justify-content: center;
        }

        /* Category List */
        .category-list {
          margin-top: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .category-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
        }

        .category-color {
          width: 12px;
          height: 12px;
          border-radius: 4px;
        }

        .category-name {
          flex: 1;
          color: #475569;
        }

        .category-revenue {
          font-weight: 600;
          color: #1e293b;
        }

        .category-growth {
          font-weight: 500;
          font-size: 11px;
        }

        .category-growth.positive {
          color: #10b981;
        }

        .category-growth.negative {
          color: #ef4444;
        }

        /* Two Column Grid */
        .two-column-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }

        /* Top Products */
        .top-products-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .product-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .product-rank {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
        }

        .product-info {
          flex: 1;
        }

        .product-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .product-stats {
          display: flex;
          gap: 12px;
          font-size: 11px;
          color: #64748b;
        }

        .product-revenue {
          font-weight: 600;
          color: #10b981;
          font-size: 13px;
        }

        .product-growth {
          font-weight: 600;
          font-size: 12px;
        }

        .product-growth.positive {
          color: #10b981;
        }

        .product-growth.negative {
          color: #ef4444;
        }

        /* Insights Card */
        .insights-card {
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          border-radius: 20px;
          padding: 24px;
        }

        .insights-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }

        .insights-header svg {
          color: #f59e0b;
        }

        .insights-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .insight-item {
          display: flex;
          gap: 14px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .insight-icon {
          font-size: 28px;
        }

        .insight-item h4 {
          font-size: 13px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px;
        }

        .insight-item p {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          line-height: 1.4;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .comparison-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .two-column-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .partner-analytics-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .comparison-grid {
            grid-template-columns: 1fr;
          }
          .insights-grid {
            grid-template-columns: 1fr;
          }
          .chart-controls {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerAnalytics;